import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

import classNames from "classnames";

import PathContants from "@routers/pathConstants";

import { useAuthStore } from "@stores/authStore";

import "./style.scss";

const SCROLL_THRESHOLD = 1;

const Header = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [isScrolled, setIsScrolled] = useState<boolean>(false);
	const [isBrandOpen, setIsBrandOpen] = useState<boolean>(false);
	const [isSupportOpen, setIsSupportOpen] = useState<boolean>(false);
	const [isMobileOpen, setIsMobileOpen] = useState<boolean>(false);
	const [isMobileBrandOpen, setIsMobileBrandOpen] = useState<boolean>(false);
	const [isMobileSupportOpen, setIsMobileSupportOpen] = useState<boolean>(false);
	const supportRef = useRef<HTMLDivElement | null>(null);
	const brandRef = useRef<HTMLDivElement | null>(null);
	const drawerRef = useRef<HTMLDivElement | null>(null);
	const burgerMenuRef = useRef<HTMLDivElement | null>(null);
	const isHome = location.pathname === PathContants.Home;
	const { isLogin, clear } = useAuthStore(["isLogin", "clear"]);
	const headerVariant = isHome ? (isScrolled ? "scrolled" : "no-scroll") : "scrolled";
	useEffect(() => {
		if (!isHome) {
			setIsScrolled(false);
			return;
		}

		const update = () => {
			setIsScrolled(window.scrollY > SCROLL_THRESHOLD);
		};
		update();
		window.addEventListener("scroll", update, { passive: true });
		return () => window.removeEventListener("scroll", update);
	}, [isHome]);

	// 외부 클릭/ESC로 서브메뉴 닫기
	useEffect(() => {
		const handlePointerDown = (e: MouseEvent | TouchEvent) => {
			const target = e.target as Node;
			if (isSupportOpen) {
				if (supportRef.current && !supportRef.current.contains(target)) {
					setIsSupportOpen(false);
				}
			}
			if (isBrandOpen) {
				if (brandRef.current && !brandRef.current.contains(target)) {
					setIsBrandOpen(false);
				}
			}
			if (isMobileOpen) {
				const clickedInsideBurger = burgerMenuRef.current?.contains(target) ?? false;
				const clickedInsideDrawer = drawerRef.current?.contains(target) ?? false;
				if (!clickedInsideBurger && !clickedInsideDrawer) {
					setIsMobileOpen(false);
				}
			}
		};

		document.addEventListener("mousedown", handlePointerDown);
		document.addEventListener("touchstart", handlePointerDown);
		return () => {
			document.removeEventListener("mousedown", handlePointerDown);
			document.removeEventListener("touchstart", handlePointerDown);
		};
	}, [isSupportOpen, isBrandOpen, isMobileOpen]);

	// 라우트 변경시 닫기
	useEffect(() => {
		setIsSupportOpen(false);
		setIsBrandOpen(false);
		setIsMobileOpen(false);
		// 페이지 이동 시 모든 서브메뉴/드로어 하위 메뉴 닫기
		setIsMobileSupportOpen(false);
		setIsMobileBrandOpen(false);
	}, [location.pathname, location.hash]);

	const handleMobileOpen = () => {
		setIsMobileOpen((v) => !v);
	};

	const handleMobileLoginClick = () => {
		if (isLogin) {
			navigate(PathContants.MeDetail);
		} else {
			navigate(PathContants.Login);
		}
	};

	const handleBrandMenuChange = () => {
		setIsBrandOpen((v) => !v);
	};

	const handleSupportMenuChange = () => {
		setIsSupportOpen((v) => !v);
	};

	const closeDesktopSubmenus = () => {
		setIsSupportOpen(false);
		setIsBrandOpen(false);
	};

	const adminPageUrl = import.meta.env.VITE_ADMIN_PAGE;

	return (
		<header
			className={classNames("header", `header--${headerVariant}`, {
				fixed: isHome,
			})}
		>
			<div className="header__inner">
				<div className="header-user-menu mobile">
					<img
						src="/images/people-icon.png"
						alt="로그인"
						onClick={handleMobileLoginClick}
						draggable={false}
					/>
				</div>
				<div className="header__brand">
					<Link to={PathContants.Home} aria-label="홈">
						<img src="/images/header-logo.png" alt="로고" draggable={false} />
					</Link>
				</div>
				<div
					className={classNames("header-burger-menu mobile", { open: isMobileOpen })}
					role="button"
					tabIndex={0}
					aria-label={isMobileOpen ? "메뉴 닫기" : "모바일 메뉴 열기"}
					ref={burgerMenuRef}
					aria-expanded={isMobileOpen}
					title={isMobileOpen ? "닫기" : "메뉴"}
					onClick={handleMobileOpen}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") handleMobileOpen();
					}}
				>
					<span />
					<span />
					<span />
				</div>

				<nav className="header__nav" aria-label="주요 메뉴">
					<ul>
						<li>
							<div
								ref={brandRef}
								className={classNames("has-submenu", { "is-open": isBrandOpen })}
								onMouseEnter={() => setIsBrandOpen(true)}
								onMouseLeave={() => setIsBrandOpen(false)}
							>
								<button
									type="button"
									className={classNames("submenu__link", {
										active: isBrandOpen,
									})}
									aria-haspopup="menu"
									aria-expanded={isBrandOpen}
									aria-controls="brand-submenu"
									onClick={handleBrandMenuChange}
								>
									Brand
								</button>

								<div
									id="brand-submenu"
									className="submenu"
									role="menu"
									aria-label="Brand submenu"
								>
									<NavLink
										to={`${PathContants.Brand}#ir-92`}
										className={() =>
											classNames("submenu__link", {
												active:
													location.pathname === PathContants.Brand &&
													location.hash === "#ir-92",
											})
										}
										role="menuitem"
										onClick={closeDesktopSubmenus}
									>
										ir-92
									</NavLink>
									<NavLink
										to={`${PathContants.Brand}#ir-85`}
										className={() =>
											classNames("submenu__link", {
												active:
													location.pathname === PathContants.Brand &&
													location.hash === "#ir-85",
											})
										}
										role="menuitem"
										onClick={closeDesktopSubmenus}
									>
										ir-85
									</NavLink>
								</div>
							</div>
						</li>
						<li>
							<NavLink
								to={PathContants.PPF}
								className={({ isActive }) =>
									classNames("submenu__link", {
										active: isActive,
									})
								}
							>
								PPF
							</NavLink>
						</li>
						<li>
							<NavLink
								to={PathContants.Partner}
								className={({ isActive }) =>
									classNames("submenu__link", { active: isActive })
								}
							>
								Partner
							</NavLink>
						</li>
						<li>
							<div
								ref={supportRef}
								className={classNames("has-submenu", { "is-open": isSupportOpen })}
								onMouseEnter={() => setIsSupportOpen(true)}
								onMouseLeave={() => setIsSupportOpen(false)}
							>
								<button
									type="button"
									className={classNames("submenu__link", {
										active: isSupportOpen,
									})}
									aria-haspopup="menu"
									aria-expanded={isSupportOpen}
									aria-controls="support-submenu"
									onClick={handleSupportMenuChange}
								>
									Support
								</button>

								<div
									id="support-submenu"
									className="submenu"
									role="menu"
									aria-label="Support submenu"
								>
									<NavLink
										to={PathContants.SupportNotice}
										className={({ isActive }) =>
											classNames("submenu__link", { active: isActive })
										}
										role="menuitem"
										onClick={closeDesktopSubmenus}
									>
										공지사항
									</NavLink>
									<NavLink
										to={PathContants.SupportContact}
										className={({ isActive }) =>
											classNames("submenu__link", { active: isActive })
										}
										role="menuitem"
										onClick={closeDesktopSubmenus}
									>
										문의하기
									</NavLink>
									<NavLink
										to={PathContants.SupportCertificate}
										className={({ isActive }) =>
											classNames("submenu__link", { active: isActive })
										}
										role="menuitem"
										onClick={closeDesktopSubmenus}
									>
										보증서 확인
									</NavLink>
								</div>
							</div>
						</li>
					</ul>
				</nav>

				<div className="header__actions">
					{isLogin ? (
						<div className="header_after-login">
							<Link to={PathContants.MeDetail} className="header__login">
								<img
									src="/images/people-icon.png"
									alt="마이페이지"
									draggable={false}
								/>
								마이페이지
							</Link>

							<Link
								to={PathContants.Login}
								className="header__login"
								onClick={() => clear()}
							>
								로그아웃
							</Link>
							<Link to={adminPageUrl} className="header__login admin-page">
								관리자 페이지
							</Link>
						</div>
					) : (
						<Link to={PathContants.Login} className="header__login">
							<img src="/images/people-icon.png" alt="로그인" draggable={false} />
							로그인
						</Link>
					)}
				</div>
			</div>

			<div
				className={classNames("mobile-overlay", { open: isMobileOpen })}
				onClick={() => setIsMobileOpen(false)}
			/>
			<div ref={drawerRef} className={classNames("mobile-drawer", { open: isMobileOpen })}>
				<nav className="drawer__nav" aria-label="모바일 내비게이션">
					<ul>
						<li>
							<NavLink
								to={PathContants.Home}
								className={({ isActive }) =>
									classNames("drawer__link", { active: isActive })
								}
								onClick={() => setIsMobileOpen(false)}
							>
								HOME
							</NavLink>
						</li>

						<li
							className={classNames("drawer__item", "has-submenu", {
								open: isMobileBrandOpen,
							})}
						>
							<button
								type="button"
								className={classNames("drawer__top-link", {
									active: location.pathname === PathContants.Brand,
								})}
								aria-expanded={isMobileBrandOpen}
								onClick={() => setIsMobileBrandOpen((v) => !v)}
							>
								<span>BRAND</span>
							</button>
							<div className="drawer__submenu">
								<NavLink
									to={`${PathContants.Brand}#ir-92`}
									className={() =>
										classNames("drawer__sublink", {
											active:
												location.pathname === PathContants.Brand &&
												location.hash === "#ir-92",
										})
									}
									onClick={() => setIsMobileOpen(false)}
								>
									ir-92
								</NavLink>
								<NavLink
									to={`${PathContants.Brand}#ir-85`}
									className={() =>
										classNames("drawer__sublink", {
											active:
												location.pathname === PathContants.Brand &&
												location.hash === "#ir-85",
										})
									}
									onClick={() => setIsMobileOpen(false)}
								>
									ir-85
								</NavLink>
							</div>
						</li>
						<li>
							<NavLink
								to={PathContants.PPF}
								className={({ isActive }) =>
									classNames("drawer__link", { active: isActive })
								}
								onClick={() => setIsMobileOpen(false)}
							>
								PPF
							</NavLink>
						</li>
						<li>
							<NavLink
								to={PathContants.Partner}
								className={({ isActive }) =>
									classNames("drawer__link", { active: isActive })
								}
								onClick={() => setIsMobileOpen(false)}
							>
								PARTNER
							</NavLink>
						</li>

						<li
							className={classNames("drawer__item", "has-submenu", {
								open: isMobileSupportOpen,
							})}
						>
							<button
								type="button"
								className={classNames("drawer__top-link", {
									active: [
										PathContants.SupportNotice,
										PathContants.SupportContact,
										PathContants.SupportCertificate,
									].includes(location.pathname),
								})}
								aria-expanded={isMobileSupportOpen}
								onClick={() => setIsMobileSupportOpen((v) => !v)}
							>
								<span>SUPPORT</span>
							</button>
							<div className="drawer__submenu">
								<NavLink
									to={PathContants.SupportNotice}
									className={({ isActive }) =>
										classNames("drawer__sublink", { active: isActive })
									}
									onClick={() => setIsMobileOpen(false)}
								>
									공지사항
								</NavLink>
								<NavLink
									to={PathContants.SupportContact}
									className={({ isActive }) =>
										classNames("drawer__sublink", { active: isActive })
									}
									onClick={() => setIsMobileOpen(false)}
								>
									문의하기
								</NavLink>
								<NavLink
									to={PathContants.SupportCertificate}
									className={({ isActive }) =>
										classNames("drawer__sublink", { active: isActive })
									}
									onClick={() => setIsMobileOpen(false)}
								>
									보증서 확인
								</NavLink>
							</div>
						</li>

						{!isLogin ? (
							<li>
								<NavLink
									to={PathContants.Login}
									className={({ isActive }) =>
										classNames("drawer__link", { active: isActive })
									}
									onClick={() => setIsMobileOpen(false)}
								>
									로그인
								</NavLink>
							</li>
						) : (
							<li>
								<span
									className="drawer__link"
									onClick={() => {
										clear();
										setIsMobileOpen(false);
										navigate(PathContants.Home);
									}}
								>
									로그아웃
								</span>
							</li>
						)}
					</ul>
				</nav>
			</div>
		</header>
	);
};

export default Header;
